/**
 * Shared guards for document-level keyboard shortcuts: the workspace's
 * single-key ones (`A` accept, `?` shortcuts dialog, `=` value bar) and the
 * app-wide chords (Cmd/Ctrl+B sidebar, Cmd/Ctrl+K palette, Cmd/Ctrl+Shift+F
 * Sean).
 *
 * A Radix menu keeps focus inside `role="menu"` and consumes letter keys for
 * typeahead, so a shortcut must stay quiet while a menu is open: otherwise
 * one keystroke would both typeahead the menu and act on the focused cell.
 */
export function isWithinMenu(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return target.closest('[role="menu"]') !== null;
}

/**
 * Whether the key belongs to the field the user is typing in: a form control,
 * a contenteditable region, or an ARIA text, search or combobox widget. A
 * shortcut stays quiet there so typing a character, or a chord such as
 * Cmd/Ctrl+B for bold, never also acts on the page.
 */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  ) {
    return true;
  }
  if (target.isContentEditable) return true;
  const role = target.getAttribute('role');
  return role === 'textbox' || role === 'combobox' || role === 'searchbox';
}
